import { useCallback, useState } from 'react';
import { BrowserProvider, Contract, id as ethersId, toUtf8Bytes, ZeroAddress } from 'ethers';
import { useWeb3 } from '../contexts/Web3Context';
import { useNetwork } from '../contexts/NetworkContext';
import { DID_REGISTRY_ABI } from '../contracts/DIDRegistryABI';

// Build-time env fallbacks — the runtime chain registry (NetworkContext) wins.
const ENV_CONTRACT_ADDRESS = import.meta.env.VITE_DID_REGISTRY_ADDRESS as string | undefined;
const ENV_CHAIN_ID = Number(import.meta.env.VITE_BLOCKCHAIN_CHAIN_ID ?? 99999);

export interface TxResult {
  txHash: string;
  blockNumber: number;
}

export interface UseDIDRegistryReturn {
  createDID: (did: string, document: object) => Promise<TxResult>;
  didExistsOnChain: (did: string) => Promise<boolean>;
  updateDIDDocument: (did: string, document: object) => Promise<TxResult>;
  deactivateDID: (did: string) => Promise<TxResult>;
  addDelegate: (did: string, delegateType: string, delegate: string, validitySeconds: number) => Promise<TxResult>;
  revokeDelegate: (did: string, delegateType: string, delegate: string) => Promise<TxResult>;
  pending: boolean;
  contractAddress: string | undefined;
  isReady: boolean;
}

/** keccak256 a string to bytes32 — matches the Go `stringToBytes32` helper. */
function toBytes32(value: string): string {
  return ethersId(value);
}

/** Serialize a DID document object to UTF-8 bytes for on-chain storage. */
function encodeDocument(doc: object): Uint8Array {
  return toUtf8Bytes(JSON.stringify(doc));
}

export function useDIDRegistry(): UseDIDRegistryReturn {
  const { signer, isConnected, isWrongNetwork, chainId } = useWeb3();
  const { selected } = useNetwork();
  const [pending, setPending] = useState(false);

  // Multi-chain: the selected network's registry address/chainId take priority.
  const CONTRACT_ADDRESS = selected?.contracts?.didRegistry || ENV_CONTRACT_ADDRESS;
  const CHAIN_ID = selected?.chainId ?? ENV_CHAIN_ID;

  const isReady =
    isConnected &&
    !isWrongNetwork &&
    !!signer &&
    !!CONTRACT_ADDRESS &&
    chainId === CHAIN_ID;

  const getContract = useCallback(() => {
    if (!signer || !CONTRACT_ADDRESS) {
      throw new Error('Wallet not connected or contract address not configured');
    }
    return new Contract(CONTRACT_ADDRESS, DID_REGISTRY_ABI, signer);
  }, [signer, CONTRACT_ADDRESS]);

  const sendTx = useCallback(
    async (fn: () => Promise<{ hash: string; wait: () => Promise<{ blockNumber: number }> }>): Promise<TxResult> => {
      setPending(true);
      try {
        const tx = await fn();
        const receipt = await tx.wait();
        return { txHash: tx.hash, blockNumber: receipt.blockNumber };
      } finally {
        setPending(false);
      }
    },
    [],
  );

  const didExistsOnChain = useCallback(
    async (did: string): Promise<boolean> => {
      if (!CONTRACT_ADDRESS) return false;
      // Use a read-only provider so we don't need a signer for the check.
      const provider = signer?.provider ?? (window.ethereum ? new BrowserProvider(window.ethereum) : null);
      if (!provider) return false;
      try {
        const contract = new Contract(CONTRACT_ADDRESS, DID_REGISTRY_ABI, provider);
        const [, owner] = await contract.resolveDID(did);
        return owner !== ZeroAddress;
      } catch {
        // resolveDID reverts when the DID does not exist — treat as not found.
        return false;
      }
    },
    [signer, CONTRACT_ADDRESS],
  );

  const createDID = useCallback(
    async (did: string, document: object): Promise<TxResult> => {
      const exists = await didExistsOnChain(did);
      if (exists) throw new Error('DID already registered on-chain for this wallet address.');
      const contract = getContract();
      return sendTx(() => contract.createDID(did, encodeDocument(document)));
    },
    [getContract, sendTx, didExistsOnChain],
  );

  const updateDIDDocument = useCallback(
    async (did: string, document: object): Promise<TxResult> => {
      const contract = getContract();
      return sendTx(() => contract.updateDIDDocument(did, encodeDocument(document)));
    },
    [getContract, sendTx],
  );

  const deactivateDID = useCallback(
    async (did: string): Promise<TxResult> => {
      const contract = getContract();
      return sendTx(() => contract.deactivateDID(did));
    },
    [getContract, sendTx],
  );

  const addDelegate = useCallback(
    async (did: string, delegateType: string, delegate: string, validitySeconds: number): Promise<TxResult> => {
      const contract = getContract();
      const delegateTypeBytes32 = toBytes32(delegateType);
      return sendTx(() => contract.addDelegate(did, delegateTypeBytes32, delegate, validitySeconds));
    },
    [getContract, sendTx],
  );

  const revokeDelegate = useCallback(
    async (did: string, delegateType: string, delegate: string): Promise<TxResult> => {
      const contract = getContract();
      const delegateTypeBytes32 = toBytes32(delegateType);
      return sendTx(() => contract.revokeDelegate(did, delegateTypeBytes32, delegate));
    },
    [getContract, sendTx],
  );

  return {
    createDID,
    didExistsOnChain,
    updateDIDDocument,
    deactivateDID,
    addDelegate,
    revokeDelegate,
    pending,
    contractAddress: CONTRACT_ADDRESS,
    isReady,
  };
}
